// src/pages/SubscriptionManagement.js
// Account & Billing — practitioner subscription management.
// Handles: current plan display, cancellation flow (OP1), tier change (OP5).
// All Stripe Billing calls simulated via Firestore. See TODO:STRIPE_BILLING comments.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../config/firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { STRIPE_PLANS, TIER_DISPLAY, DOWNGRADE_LOSSES, DOWNGRADE_KEPT } from '../config/stripe-plans';
import { trackEvent } from '../utils/analytics';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

const CANCEL_REASONS = [
  'Too expensive',
  'Not enough clients or bookings',
  'Missing features I need',
  'Found a different platform',
  'Taking a break from my practice',
  'Technical issues',
  'Other',
];

// ─────────────────────────────────────────────────────────────────────────────
// CancelModal — 3-step cancellation flow
// ─────────────────────────────────────────────────────────────────────────────

const CancelModal = ({ tier, currentPeriodEnd, onClose, onCancelled }) => {
  const [step, setStep] = useState(1); // 1 = warning, 2 = reason, 3 = confirmed
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tierName = TIER_DISPLAY[tier] || tier;
  const periodEnd = formatDate(currentPeriodEnd) || 'the end of your billing period';

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelSubscription({ reason: reason || null, feedback: feedback.trim() || null });
      onCancelled(result.data.currentPeriodEnd);
      setStep(3);
    } catch (err) {
      console.error('Cancel subscription failed:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && step !== 3) onClose(); }}
    >
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px',
        padding: '32px', fontFamily: "'Montserrat', sans-serif",
      }}>
        {/* Step 1 — Warning */}
        {step === 1 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
              Cancel your subscription?
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', lineHeight: 1.6 }}>
              Your <strong>{tierName}</strong> plan will remain active until <strong>{periodEnd}</strong>. After that:
            </p>
            <ul style={{ margin: '0 0 24px 0', padding: '0 0 0 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                'Your website will be unpublished (content saved, not deleted)',
                'Active bookings will still be honored',
                'Client data and session history are fully preserved',
                'Directory listing moves to a basic profile',
                'You can resubscribe anytime to restore everything',
              ].map((item) => (
                <li key={item} style={{ fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>{item}</li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px 16px', border: '1.5px solid #D1D5DB',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Keep my subscription
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                style={{
                  flex: 1, padding: '10px 16px', border: 'none',
                  borderRadius: '8px', background: '#fee2e2', color: '#dc2626',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Continue →
              </button>
            </div>
          </>
        )}

        {/* Step 2 — Reason survey */}
        {step === 2 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
              We're sorry to see you go.
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', lineHeight: 1.6 }}>
              Would you tell us why? <span style={{ color: '#9CA3AF' }}>(optional)</span>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              {CANCEL_REASONS.map((r) => (
                <label
                  key={r}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    style={{ width: '16px', height: '16px', accentColor: '#7c3aed', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: '#374151' }}>{r}</span>
                </label>
              ))}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Anything else? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span>
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Your feedback helps us improve..."
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '14px',
                  border: '1.5px solid #D1D5DB', borderRadius: '8px', outline: 'none',
                  resize: 'none', boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif",
                }}
              />
            </div>
            {error && (
              <div style={{ padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{error}</p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setStep(1)}
                onKeyDown={(e) => e.key === 'Enter' && setStep(1)}
                style={{
                  flex: 1, padding: '10px 16px', border: '1.5px solid #D1D5DB',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  textAlign: 'center', userSelect: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Go back
              </span>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                style={{
                  flex: 1, padding: '10px 16px', border: 'none',
                  borderRadius: '8px', background: loading ? '#fee2e2' : '#dc2626',
                  color: 'white', fontSize: '14px', fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {loading ? 'Cancelling...' : 'Confirm cancellation'}
              </button>
            </div>
          </>
        )}

        {/* Step 3 — Confirmation */}
        {step === 3 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%', background: '#D1FAE5',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <CheckCircle style={{ width: '26px', height: '26px', color: '#059669' }} />
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
                Subscription cancelled.
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>
                You'll continue to have full access until <strong>{periodEnd}</strong>.
                We sent a confirmation to your email.
              </p>
            </div>
            <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center', marginBottom: '20px' }}>
              Changed your mind?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  width: '100%', padding: '11px', border: 'none',
                  borderRadius: '8px', background: '#7c3aed', color: 'white',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Reactivate subscription
              </button>
              <span
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Enter' && onClose()}
                style={{
                  width: '100%', padding: '11px', border: '1.5px solid #D1D5DB',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  textAlign: 'center', userSelect: 'none', boxSizing: 'border-box',
                  display: 'block', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Return to settings
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ChangePlanModal — Downgrade warning + upgrade confirmation
// ─────────────────────────────────────────────────────────────────────────────

const ChangePlanModal = ({ currentTier, targetTier, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const tierOrder = { free: 0, booking: 1, pro: 2, elite: 3 };
  const isUpgrade = (tierOrder[targetTier] ?? 0) > (tierOrder[currentTier] ?? 0);
  const lossKey = `${currentTier}->${targetTier}`;
  const losses = DOWNGRADE_LOSSES[lossKey] || [];
  const kept = DOWNGRADE_KEPT[lossKey] || [];
  const currentPlan = STRIPE_PLANS[currentTier];
  const targetPlan = STRIPE_PLANS[targetTier];

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const changeSubscriptionTier = httpsCallable(functions, 'changeSubscriptionTier');
      const result = await changeSubscriptionTier({ newTier: targetTier });
      if (result.data.isUpgrade) {
        localStorage.setItem('userTier', targetTier);
        window.dispatchEvent(new Event('storage'));
      }
      onChanged(result.data);
      setConfirmed(true);
      trackEvent('subscription_started', { tier: targetTier });
    } catch (err) {
      console.error('Change tier failed:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !confirmed) onClose(); }}
    >
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px',
        padding: '32px', fontFamily: "'Montserrat', sans-serif",
      }}>
        {!confirmed ? (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
              {isUpgrade ? `Upgrade to ${targetPlan.name}` : `Downgrade to ${targetPlan.name}?`}
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', lineHeight: 1.6 }}>
              Moving from <strong>{currentPlan.name}</strong> (${currentPlan.price}/mo) to{' '}
              <strong>{targetPlan.name}</strong> ({targetPlan.price === 0 ? 'Free' : `$${targetPlan.price}/mo`})
            </p>

            {!isUpgrade && losses.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#DC2626', marginBottom: '8px' }}>
                  Features you'll lose:
                </p>
                <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {losses.map((f) => (
                    <li key={f} style={{ fontSize: '13px', color: '#374151' }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            {kept.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#059669', marginBottom: '8px' }}>
                  Features you'll keep:
                </p>
                <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {kept.map((f) => (
                    <li key={f} style={{ fontSize: '13px', color: '#374151' }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{
              padding: '12px 14px',
              background: isUpgrade ? '#f5f3ff' : '#fffbeb',
              border: `1px solid ${isUpgrade ? '#ddd6fe' : '#fde68a'}`,
              borderRadius: '8px', marginBottom: '20px',
            }}>
              <p style={{ fontSize: '13px', color: isUpgrade ? '#5b21b6' : '#92400e', margin: 0, lineHeight: 1.5 }}>
                {isUpgrade
                  ? 'Upgrade takes effect immediately. All new features are available right away.'
                  : 'Your data is never deleted. Changes take effect at the end of your current billing period. You keep full access until then.'}
              </p>
            </div>

            {error && (
              <div style={{ padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <span
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Enter' && onClose()}
                style={{
                  flex: 1, padding: '10px 16px', border: '1.5px solid #D1D5DB',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  textAlign: 'center', userSelect: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {isUpgrade ? 'Cancel' : `Keep ${currentPlan.name}`}
              </span>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                style={{
                  flex: 1, padding: '10px 16px', border: 'none',
                  borderRadius: '8px',
                  background: loading ? '#c4b5fd' : '#7c3aed',
                  color: 'white', fontSize: '14px', fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {loading ? 'Processing...' : isUpgrade ? `Upgrade to ${targetPlan.name}` : `Downgrade to ${targetPlan.name}`}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '50%', background: '#faf5eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <CheckCircle style={{ width: '26px', height: '26px', color: '#bb9749' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
              {isUpgrade ? 'Plan updated successfully' : 'Downgrade scheduled'}
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6, marginBottom: '24px' }}>
              {isUpgrade
                ? `You're now on the ${targetPlan.name} plan. Your new features are available immediately.`
                : `Your plan will change to ${targetPlan.name} at the end of your billing period. You keep full access until then.`}
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '11px 32px', border: 'none',
                borderRadius: '8px', background: '#7c3aed', color: 'white',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Back to dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SubscriptionManagement — main page
// ─────────────────────────────────────────────────────────────────────────────

const SubscriptionManagement = () => {
  const [currentTier, setCurrentTier] = useState(() => {
    const dev = localStorage.getItem('dev_subscription_tier');
    return dev || localStorage.getItem('userTier') || 'free';
  });
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [changePlanTarget, setChangePlanTarget] = useState(null);
  const [showPaymentHelp, setShowPaymentHelp] = useState(false);

  // Load subscription state from Firestore (live)
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setLoadingData(false); }
    }, 4000);

    const { onAuthStateChanged } = require('firebase/auth');
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!done && user) {
        done = true;
        clearTimeout(timeout);
        const practRef = doc(db, 'practitioners', user.uid);
        const unsubFirestore = onSnapshot(practRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const firestoreTier = data.subscriptionTier || 'free';
            setCurrentTier(firestoreTier);
            localStorage.setItem('userTier', firestoreTier);
            setSubscriptionData(data.subscription || null);
          }
          setLoadingData(false);
        }, (err) => {
          console.warn('⚠️ Firestore subscription read failed:', err);
          setLoadingData(false);
        });
        return () => unsubFirestore();
      } else if (!done && user === null) {
        done = true;
        clearTimeout(timeout);
        setLoadingData(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsubAuth(); };
  }, []);

  // Sync tier from AdminTierOverride / storage events
  useEffect(() => {
    const handleStorage = () => {
      const dev = localStorage.getItem('dev_subscription_tier');
      const stored = localStorage.getItem('userTier');
      setCurrentTier(dev || stored || 'free');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleCancelled = (newPeriodEnd) => {
    setSubscriptionData(prev => ({ ...(prev || {}), cancelAtPeriodEnd: true, currentPeriodEnd: newPeriodEnd }));
  };

  const handleReactivate = async () => {
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const reactivate = httpsCallable(functions, 'reactivateSubscription');
      await reactivate({});
      setSubscriptionData(prev => ({
        ...(prev || {}), cancelAtPeriodEnd: false, cancelledAt: null, cancelReason: null,
      }));
    } catch (err) {
      console.error('Reactivate failed:', err);
      alert(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleChangePlanResult = (result) => {
    if (result.isUpgrade) {
      setCurrentTier(result.newTier);
      setSubscriptionData(prev => ({
        ...(prev || {}), tier: result.newTier, pendingDowngrade: null,
        cancelAtPeriodEnd: false,
      }));
    } else {
      setSubscriptionData(prev => ({
        ...(prev || {}),
        pendingDowngrade: { toTier: result.newTier, effectiveDate: result.effectiveDate },
      }));
    }
  };

  const openChangePlan = (targetTier) => {
    setChangePlanTarget(targetTier);
    setShowChangePlanModal(true);
  };

  const cancelAtPeriodEnd = subscriptionData?.cancelAtPeriodEnd || false;
  const pendingDowngrade = subscriptionData?.pendingDowngrade || null;
  const currentPeriodEnd = subscriptionData?.currentPeriodEnd || null;
  const currentPlan = STRIPE_PLANS[currentTier] || STRIPE_PLANS.free;
  const isFree = currentTier === 'free';

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Plan & Billing</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Plan & Billing</h1>
          <p className="text-gray-500 text-sm">Manage your subscription plan</p>
        </div>

        {/* ── Current Plan Card ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Current plan</h2>

          {/* Pending cancellation banner */}
          {cancelAtPeriodEnd && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 14px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px',
            }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', margin: '0 0 2px 0' }}>
                  Cancelling on {formatDate(currentPeriodEnd) || 'your next billing date'}
                </p>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                  You'll move to the free Essentials tier. Full access until then.
                </p>
              </div>
            </div>
          )}

          {/* Pending downgrade banner */}
          {!cancelAtPeriodEnd && pendingDowngrade && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 14px', background: '#f5f3ff',
              border: '1px solid #ddd6fe', borderRadius: '8px', marginBottom: '16px',
            }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#7c3aed', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#5b21b6', margin: '0 0 2px 0' }}>
                  Changing to {TIER_DISPLAY[pendingDowngrade.toTier]} on {formatDate(pendingDowngrade.effectiveDate) || 'your next billing date'}
                </p>
                <p style={{ fontSize: '13px', color: '#5b21b6', margin: 0 }}>
                  Full access to your current features until then.
                </p>
              </div>
            </div>
          )}

          {/* Payment past due banner */}
          {subscriptionData?.paymentStatus === 'past_due' && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 14px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px',
            }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', margin: '0 0 2px 0' }}>
                  Payment past due
                </p>
                <p style={{ fontSize: '13px', color: '#92400e', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                  We were unable to process your payment. Update your payment method to keep your plan.
                  {subscriptionData?.autoDowngradeAt && (
                    <> Your account will move to the free tier on{' '}
                      <strong>{formatDate(subscriptionData.autoDowngradeAt)}</strong>.
                    </>
                  )}
                </p>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    const el = document.getElementById('payment-method-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const el = document.getElementById('payment-method-section');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  style={{
                    fontSize: '13px', fontWeight: 600, color: '#d97706',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  Update payment method ↓
                </span>
              </div>
            </div>
          )}

          {/* Plan info row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>{currentPlan.name}</span>
                {!isFree && (
                  <span style={{ fontSize: '15px', color: '#6B7280' }}>${currentPlan.price}/month</span>
                )}
              </div>
              {currentPeriodEnd && !isFree && (
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                  {cancelAtPeriodEnd ? 'Access ends' : 'Next billing date'}:{' '}
                  <strong>{formatDate(currentPeriodEnd)}</strong>
                </p>
              )}
              {isFree && (
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>Up to 3 bookings per month</p>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {cancelAtPeriodEnd ? (
                <button
                  type="button"
                  onClick={handleReactivate}
                  style={{
                    padding: '8px 16px', border: 'none', borderRadius: '8px',
                    background: '#7c3aed', color: 'white', fontSize: '13px',
                    fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Reactivate subscription
                </button>
              ) : !isFree ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowCancelModal(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowCancelModal(true)}
                  style={{
                    padding: '8px 16px', border: '1.5px solid #e5e7eb',
                    borderRadius: '8px', background: 'white', color: '#dc2626',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    userSelect: 'none', fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Cancel subscription
                </span>
              ) : null}
            </div>
          </div>

        </div>

        {/* ── Payment Method Section ── */}
        {!isFree && (
          <div id="payment-method-section" className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Payment method</h2>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <CreditCard style={{ width: '18px', height: '18px', color: '#9CA3AF', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                  Payment processing is managed by Stripe.
                </p>

                {/* TODO:STRIPE_BILLING: Replace with Stripe Customer Portal session
                    const session = await stripe.billingPortal.sessions.create({
                      customer: stripeCustomerId,
                      return_url: 'https://divineframeworks.com/dashboard/subscription',
                    });
                    window.location.href = session.url;
                */}
                {showPaymentHelp ? (
                  <div style={{
                    padding: '12px 14px', background: '#f9fafb',
                    border: '1px solid #E5E7EB', borderRadius: '8px',
                  }}>
                    <p style={{ fontSize: '13px', color: '#374151', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                      To update your payment method, please contact us at{' '}
                      <a href="mailto:support@divineframeworks.com" style={{ color: '#7c3aed', fontWeight: 600 }}>
                        support@divineframeworks.com
                      </a>{' '}
                      and we'll help you right away.
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowPaymentHelp(false)}
                      onKeyDown={(e) => e.key === 'Enter' && setShowPaymentHelp(false)}
                      style={{
                        fontSize: '12px', color: '#9CA3AF', cursor: 'pointer', userSelect: 'none',
                      }}
                    >
                      Dismiss
                    </span>
                  </div>
                ) : (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowPaymentHelp(true)}
                    onKeyDown={(e) => e.key === 'Enter' && setShowPaymentHelp(true)}
                    style={{
                      display: 'inline-block',
                      padding: '8px 18px',
                      border: '1.5px solid #E5E7EB',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#374151',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    Manage payment method →
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Change Plan Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Change plan</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            {Object.values(STRIPE_PLANS).map((plan) => {
              const isCurrent = plan.tier === currentTier;
              const tierOrder = { free: 0, booking: 1, pro: 2, elite: 3 };
              const isUpgrade = (tierOrder[plan.tier] ?? 0) > (tierOrder[currentTier] ?? 0);
              const isPendingTarget = pendingDowngrade?.toTier === plan.tier;

              return (
                <div
                  key={plan.tier}
                  style={{
                    border: `2px solid ${isCurrent ? '#bb9749' : '#E5E7EB'}`,
                    borderRadius: '10px', padding: '16px',
                    background: isCurrent ? '#fffdf7' : 'white',
                    position: 'relative',
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onClick={() => { if (!isCurrent && !cancelAtPeriodEnd) openChangePlan(plan.tier); }}
                  onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.borderColor = '#7c3aed'; }}
                  onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.borderColor = '#E5E7EB'; }}
                >
                  {isCurrent && (
                    <span style={{
                      position: 'absolute', top: '-11px', left: '12px',
                      padding: '2px 8px', background: '#bb9749', color: 'white',
                      fontSize: '11px', fontWeight: 700, borderRadius: '20px',
                    }}>
                      Current
                    </span>
                  )}
                  {isPendingTarget && !isCurrent && (
                    <span style={{
                      position: 'absolute', top: '-11px', left: '12px',
                      padding: '2px 8px', background: '#f5f3ff', color: '#7c3aed',
                      fontSize: '11px', fontWeight: 700, borderRadius: '20px',
                      border: '1px solid #ddd6fe',
                    }}>
                      Pending
                    </span>
                  )}

                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>
                      {plan.name}
                    </p>
                    <p style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
                      {plan.price === 0 ? 'Free' : `$${plan.price}`}
                      {plan.price > 0 && <span style={{ fontSize: '12px', fontWeight: 400, color: '#9CA3AF' }}>/mo</span>}
                    </p>
                  </div>

                  <ul style={{ margin: '0 0 10px 0', padding: '0 0 0 14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {plan.features.slice(0, 4).map((f) => (
                      <li key={f} style={{ fontSize: '11px', color: '#6B7280', lineHeight: 1.4 }}>{f}</li>
                    ))}
                  </ul>
                  <Link to="/pricing" style={{ fontSize: '11px', fontWeight: 600, color: '#7c3aed', textDecoration: 'none' }}>
                    See all features →
                  </Link>

                  {isCurrent ? (
                    <div style={{
                      padding: '6px', background: '#faf5eb', borderRadius: '6px',
                      textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#92400e',
                    }}>
                      Current plan
                    </div>
                  ) : cancelAtPeriodEnd ? (
                    <div style={{
                      padding: '6px', background: '#F3F4F6', borderRadius: '6px',
                      textAlign: 'center', fontSize: '11px', color: '#9CA3AF',
                    }}>
                      —
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openChangePlan(plan.tier)}
                      style={{
                        width: '100%', padding: '6px', border: 'none', borderRadius: '6px',
                        background: isUpgrade ? '#7c3aed' : '#F3F4F6',
                        color: isUpgrade ? 'white' : '#374151',
                        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {isUpgrade ? 'Upgrade' : 'Downgrade'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {cancelAtPeriodEnd && (
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '12px', textAlign: 'center' }}>
              Reactivate your subscription to change plans.
            </p>
          )}
        </div>

        {/* ── Help link ── */}
        <div style={{ textAlign: 'center', paddingBottom: '32px' }}>
          <Link
            to="/dashboard/settings/support"
            style={{ fontSize: '13px', color: '#9CA3AF', textDecoration: 'none' }}
          >
            Questions about billing?{' '}
            <span style={{ color: '#7c3aed', fontWeight: 600 }}>Visit Help Center</span>
          </Link>
        </div>
      </div>

      {/* ── Modals ── */}
      {showCancelModal && (
        <CancelModal
          tier={currentTier}
          currentPeriodEnd={currentPeriodEnd}
          onClose={() => setShowCancelModal(false)}
          onCancelled={handleCancelled}
        />
      )}

      {showChangePlanModal && changePlanTarget && (
        <ChangePlanModal
          currentTier={currentTier}
          targetTier={changePlanTarget}
          onClose={() => { setShowChangePlanModal(false); setChangePlanTarget(null); }}
          onChanged={handleChangePlanResult}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;
